import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { OnboardingStore, TourConfig } from '@/config/tours/types';
import { getTourById } from '@/config/tours';

const initialState = {
  activeTourId: null as string | null,
  currentStepIndex: 0,
  status: 'idle' as const,
  completedTours: {} as Record<string, number>,
  currentTour: null as TourConfig | null,
};

export const useOnboardingStore = create<OnboardingStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        startTour: (tourId: string) => {
          const tour = getTourById(tourId);
          if (!tour) {
            console.warn(`Tour "${tourId}" not found in registry`);
            return;
          }

          // Check if tour should be shown
          if (!get().shouldShowTour(tourId)) {
            console.log(`Tour "${tourId}" already completed or conditions not met`);
            return;
          }

          set({
            activeTourId: tourId,
            currentStepIndex: 0,
            status: 'running',
            currentTour: tour,
          });
        },

        forceStartTour: (tourId: string) => {
          const tour = getTourById(tourId);
          if (!tour) {
            console.warn(`Tour "${tourId}" not found in registry`);
            return;
          }

          // Force start without checking completion status
          set({
            activeTourId: tourId,
            currentStepIndex: 0,
            status: 'running',
            currentTour: tour,
          });
        },

        nextStep: () => {
          const { currentStepIndex, currentTour, completeTour } = get();
          if (!currentTour) return;

          const currentStep = currentTour.steps[currentStepIndex];

          // Call onAfterStep callback if defined
          if (currentStep?.onAfterStep) {
            currentStep.onAfterStep();
          }

          if (currentStepIndex >= currentTour.steps.length - 1) {
            // Last step - complete the tour
            completeTour();
          } else {
            const nextIndex = currentStepIndex + 1;
            const nextStep = currentTour.steps[nextIndex];

            // Call onBeforeStep callback if defined
            if (nextStep?.onBeforeStep) {
              nextStep.onBeforeStep();
            }

            set({ currentStepIndex: nextIndex });
          }
        },

        prevStep: () => {
          const { currentStepIndex, currentTour } = get();
          if (!currentTour || currentStepIndex <= 0) return;

          const prevIndex = currentStepIndex - 1;
          const prevStep = currentTour.steps[prevIndex];

          // Call onBeforeStep callback if defined
          if (prevStep?.onBeforeStep) {
            prevStep.onBeforeStep();
          }

          set({ currentStepIndex: prevIndex });
        },

        skipTour: () => {
          const { activeTourId, currentTour } = get();
          if (!activeTourId || !currentTour) return;

          // Mark as seen with current version to prevent re-showing
          // Tour will only show again if version is bumped
          set((state) => ({
            ...initialState,
            completedTours: {
              ...state.completedTours,
              [activeTourId]: currentTour.version,
            },
          }));
        },

        completeTour: () => {
          const { activeTourId, currentTour } = get();
          if (!activeTourId || !currentTour) return;

          set((state) => ({
            ...initialState,
            completedTours: {
              ...state.completedTours,
              [activeTourId]: currentTour.version,
            },
          }));
        },

        pauseTour: () => {
          set({ status: 'paused' });
        },

        resumeTour: () => {
          const { status } = get();
          if (status === 'paused') {
            set({ status: 'running' });
          }
        },

        goToStep: (stepIndex: number) => {
          const { currentTour } = get();
          if (!currentTour) return;

          if (stepIndex >= 0 && stepIndex < currentTour.steps.length) {
            const targetStep = currentTour.steps[stepIndex];

            // Call onBeforeStep callback if defined
            if (targetStep?.onBeforeStep) {
              targetStep.onBeforeStep();
            }

            set({ currentStepIndex: stepIndex });
          }
        },

        shouldShowTour: (tourId: string): boolean => {
          const tour = getTourById(tourId);
          if (!tour) return false;

          const { completedTours } = get();
          const seenVersion = completedTours[tourId];

          // Never seen - show it
          if (seenVersion === undefined) return true;

          // Version bumped - show it again
          if (tour.version > seenVersion) return true;

          // Check custom conditions if defined
          if (tour.conditions?.custom && !tour.conditions.custom()) {
            return false;
          }

          return false;
        },

        markTourSeen: (tourId: string, version: number) => {
          set((state) => ({
            completedTours: {
              ...state.completedTours,
              [tourId]: version,
            },
          }));
        },

        loadCompletedTours: (tours: Record<string, number>) => {
          set({ completedTours: tours });
        },

        resetAllTours: () => {
          set({ ...initialState, completedTours: {} });
        },
      }),
      {
        name: 'onboarding-storage',
        // Only persist completedTours to localStorage
        partialize: (state) => ({ completedTours: state.completedTours }),
      }
    ),
    {
      name: 'onboarding-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// Selectors for common use cases
export const selectCurrentStep = (state: OnboardingStore) =>
  state.currentTour?.steps[state.currentStepIndex] ?? null;

export const selectIsLastStep = (state: OnboardingStore) =>
  state.currentTour
    ? state.currentStepIndex >= state.currentTour.steps.length - 1
    : false;

export const selectIsFirstStep = (state: OnboardingStore) =>
  state.currentStepIndex === 0;

export const selectProgress = (state: OnboardingStore) => ({
  current: state.currentStepIndex + 1,
  total: state.currentTour?.steps.length ?? 0,
});

export const selectIsTourActive = (state: OnboardingStore) =>
  state.status === 'running' && state.currentTour !== null;
